import { getUsersByWorkEngagement } from '@/app/dashboard/technical-management/utils';
import type { FilterObject, JsonFilter } from '@/hooks/use-table-filter/types';
import { ObjectEntries } from '@/lib/utils';
import { DateTime } from 'luxon';
import { getSessionValues } from '@/providers/session-provider/get-session-values';

type RewriteResult = {
  key: string;
  value: FilterObject;
};

/**
 * Filter rewrites are definitions of filters that are only supported in frontend
 * and need to be converted to another filter that is supported in backend.
 * For example, `accepted_by_work_engagement` will fetch users in the work engagement
 * and change the filter to `users_accepted_delegations`.
 */
type FilterRewriteRule = {
  key: string;
  rewrite: (
    filter: FilterObject
  ) => Promise<RewriteResult | null> | RewriteResult | null;
};

export const CURRENT_USER_FILTER_SENTINEL = '__current_user__';

/**
 * If value contains the sentinel, replace it with userId and return the new value.
 */
function replaceSentinelWithUserId(
  value: string | number | string[] | boolean | null | undefined,
  userId: string
): string | string[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (value === CURRENT_USER_FILTER_SENTINEL) return userId;
  if (Array.isArray(value)) {
    if (!value.includes(CURRENT_USER_FILTER_SENTINEL)) return undefined;
    const replaced = value.map((v) =>
      v === CURRENT_USER_FILTER_SENTINEL ? userId : v
    );
    return [...new Set(replaced)];
  }
  return undefined;
}

/**
 * Resolve CURRENT_USER_FILTER_SENTINEL to actual userId in an id array.
 * Use in UI (RelationDisplay, RelationModal) so we never send the sentinel to APIs
 * and so the current user appears selected. If no userId, sentinel is filtered out.
 */
export function resolveCurrentUserInFilterIds(ids: string[]): string[] {
  const { userId } = getSessionValues();
  if (!userId) {
    return ids.filter((id) => id !== CURRENT_USER_FILTER_SENTINEL);
  }
  const replaced = ids.map((id) =>
    id === CURRENT_USER_FILTER_SENTINEL ? userId : id
  );
  return [...new Set(replaced)];
}

function createSentinelRewriteRule(key: string): FilterRewriteRule {
  return {
    key,
    rewrite: (filter: FilterObject) => {
      const { userId } = getSessionValues();
      const rewrittenNoop = replaceSentinelWithUserId(filter.noop, userId);
      if (rewrittenNoop === undefined) {
        return { key, value: filter };
      }
      return { key, value: { ...filter, noop: rewrittenNoop } };
    },
  };
}

function rewriteDateWindowFilter(
  key: string,
  filter: FilterObject
): RewriteResult | null {
  const today = DateTime.now();

  const parseDays = (v: unknown): number | null => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const rounded = Math.round(n);
    return rounded >= 1 ? rounded : null;
  };

  let start: DateTime | undefined;
  let end: DateTime | undefined;

  if (
    filter.__date_past == null &&
    filter.__date_future == null &&
    filter.__date_time_past == null &&
    filter.__date_time_future == null
  ) {
    return null;
  }

  const useDateOnlyFormat =
    filter.__date_past != null || filter.__date_future != null;

  const formatValue = (dt: DateTime): string =>
    useDateOnlyFormat ? (dt.toISODate() ?? '') : (dt.toISO() ?? '');

  if (filter.__date_time_past || filter.__date_past) {
    const days = parseDays(filter.__date_time_past ?? filter.__date_past);
    if (!days) return null;

    start = today
      .minus({ days: days - 1 })
      .startOf('day')
      .toUTC();
    end = today.endOf('day').toUTC();
  }

  if (filter.__date_time_future || filter.__date_future) {
    const days = parseDays(filter.__date_time_future ?? filter.__date_future);
    if (!days) return null;

    const futureStart = today.plus({ days: 1 }).startOf('day').toUTC();
    const futureEnd = today.plus({ days }).endOf('day').toUTC();

    if (start) {
      end = futureEnd;
    } else {
      start = futureStart;
      end = futureEnd;
    }
  }

  return {
    key,
    value: {
      ...(start ? { __gte: formatValue(start) } : {}),
      ...(end ? { __lte: formatValue(end) } : {}),
    },
  };
}

const filterRewrites = [
  {
    key: 'unanswered_by_work_engagement',
    rewrite: async (filter: FilterObject) => {
      const workEngagementId = Array.isArray(filter.noop)
        ? filter.noop.at(0)
        : null;
      if (!workEngagementId) {
        return null;
      }

      const userIds = await getUsersByWorkEngagement(workEngagementId, {
        queryParams: { pageSize: 30 },
      });

      return {
        key: 'users_unanswered_delegations',
        value: {
          noop: userIds,
        },
      };
    },
  },
  {
    key: 'accepted_by_work_engagement',
    rewrite: async (filter: FilterObject) => {
      const workEngagementId = Array.isArray(filter.noop)
        ? filter.noop.at(0)
        : null;
      if (!workEngagementId) {
        return null;
      }

      // Fetch a maximum of 30 because more could make the url too long.
      const userIds = await getUsersByWorkEngagement(workEngagementId, {
        queryParams: { pageSize: 30 },
      });

      return {
        key: 'users_accepted_delegations',
        value: {
          noop: userIds,
        },
      };
    },
  },
  createSentinelRewriteRule('users_unanswered_delegations'),
  createSentinelRewriteRule('users_unanswered_delegations_planner'),
  createSentinelRewriteRule('users_accepted_delegations'),
  createSentinelRewriteRule('users_accepted_delegations_planner'),
] as const satisfies FilterRewriteRule[];

export async function rewriteFilter(
  jsonFilter: JsonFilter | undefined
): Promise<JsonFilter | undefined> {
  if (!jsonFilter) {
    return undefined;
  }

  const filterEntries = ObjectEntries(jsonFilter);

  const rewritePromises = filterEntries.map(async ([key, value]) => {
    if (!value) {
      return null;
    }

    // Date window filter rewrite is applied to all date window filters and not key specific filters
    const dateWindowRewrite = rewriteDateWindowFilter(key, value);
    if (dateWindowRewrite) {
      return [dateWindowRewrite.key, dateWindowRewrite.value] as const;
    }

    const rewriteRule = filterRewrites.find((rewrite) => rewrite.key === key);

    if (rewriteRule) {
      try {
        const rewritten = await rewriteRule.rewrite(value);
        if (!rewritten) {
          return null;
        }
        return [rewritten.key, rewritten.value] as const;
      } catch {
        console.error(`Failed to rewrite filter with key "${key}"`);
        return null;
      }
    }
    return [key, value] as const;
  });

  const rewrittenEntries = await Promise.all(rewritePromises);

  return Object.fromEntries(rewrittenEntries.filter(Boolean)) as JsonFilter;
}
